import { useEffect, useState } from "react";
import { toast } from "sonner";
import client, { imgUrl } from "@/lib/glossyApi";
import {
  RefreshCw, Save, Sparkles, Plus, Trash2, Send, Eye, Image as ImageIcon,
  Megaphone, Calendar, Images, Upload,
} from "lucide-react";
import SavedImagePicker from "./SavedImagePicker";
/**
 * Noticeboard — unlimited posters per member. Each poster is a stylish corporate offer
 * with the member's logo, brand colour, offer text and CTA rendered by our template
 * (not AI text). Backend is wired via /admin/notices — this panel is a per-member
 * list with add / edit / generate-poster / save / publish / delete.
 */
export default function NoticeboardSection({ member, onChange }) {
  const m = member;
  const [notices, setNotices] = useState([]);
  const [busy, setBusy] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await client.get("/admin/notices");
      // ONE live poster at a time per member — show the most recent non-archived notice.
      const mine = (r.data || [])
        .filter((n) => n.member_id === m.id && n.status !== "archived")
        .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      setNotices(mine.slice(0, 1));
    } catch { toast.error("Couldn't load your Noticeboard."); }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [m.id]);

  const addNotice = async () => {
    if (notices.length > 0) {
      if (!window.confirm("Replace the current offer with a fresh one? The current offer will be archived (kept in history, off the public Noticeboard).")) return;
      // Archive the current one
      try { await client.patch(`/admin/notices/${notices[0].id}`, { status: "archived" }); }
      catch { /* non-fatal */ }
    }
    setBusy("add");
    try {
      const r = await client.post("/admin/notices", {
        member_id: m.id,
        member_name: m.business_name || "",
        borough: m.borough || "",
        type: "offer",
        title: "New offer — edit me",
        body: "Add a short line describing the offer. Keep it punchy.",
        accent: m.accent_color || "#141414",
        status: "draft",
      });
      setNotices([r.data]);
      toast.success("Fresh offer ready. Fill it in, generate the poster, then publish.");
      onChange && onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't create the offer."); }
    setBusy("");
  };

  const removeNotice = async (id) => {
    if (!window.confirm("Delete this offer permanently?")) return;
    setBusy(`del-${id}`);
    try {
      await client.delete(`/admin/notices/${id}`);
      setNotices(notices.filter((n) => n.id !== id));
      toast.success("Offer deleted.");
      onChange && onChange();
    } catch { toast.error("Couldn't delete."); }
    setBusy("");
  };

  return (
    <section id="section-noticeboard" data-testid="noticeboard-section" className="space-y-4 pt-4 scroll-mt-24">
      <div className="bg-ink text-cream px-5 py-3 flex flex-wrap items-baseline gap-x-4 gap-y-1 grain relative">
        <span className="overline text-gloss-gold relative z-10">Noticeboard · unlimited offer posters</span>
      </div>

      <div className="border-2 border-gloss-gold/30 bg-gloss-gold/5 p-4" data-testid="noticeboard-headline">
        <p className="font-serif text-lg text-ink">One live offer at a time — unlimited over time.</p>
        <p className="font-sans text-sm text-ink/60 mt-1">Members carry <b>one active poster</b> on the Noticeboard at any moment. Replace it as often as they like — <b>minimum one fresh offer per month</b>. Every poster carries their real logo, brand colour, offer text and CTA — nothing AI-drawn, everything readable.</p>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="font-mono text-[11px] text-ink/50" data-testid="noticeboard-count">{notices.length === 0 ? "No live offer" : "1 live offer"}</p>
        <button onClick={addNotice} disabled={!!busy}
          className="bg-ink text-cream px-4 py-2.5 overline flex items-center gap-2 hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-60" data-testid="noticeboard-add-btn">
          {busy === "add" ? <RefreshCw size={14} className="animate-spin" /> : <Plus size={14} />} {notices.length === 0 ? "Create offer" : "Post a new offer"}
        </button>
      </div>

      {loading ? (
        <p className="font-mono text-[11px] text-ink/40 text-center py-8">Loading offers…</p>
      ) : notices.length === 0 ? (
        <div className="border border-dashed border-ink/20 p-8 text-center" data-testid="noticeboard-empty">
          <Megaphone size={28} className="text-ink/25 mx-auto mb-3" />
          <p className="font-serif text-lg text-ink/60">No live offer yet.</p>
          <p className="font-mono text-[11px] text-ink/45 mt-1">Click <b>Create offer</b> to design their first Noticeboard poster.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {notices.map((n) => (
            <NoticeCard key={n.id} notice={n} member={m} onRemove={() => removeNotice(n.id)}
              onUpdated={(updated) => { setNotices(notices.map((x) => x.id === updated.id ? updated : x)); onChange && onChange(); }}
              busy={busy} setBusy={setBusy} />
          ))}
        </div>
      )}
    </section>
  );
}

function NoticeCard({ notice, member, onRemove, onUpdated, busy, setBusy }) {
  const [type, setType] = useState(notice.type || "offer");
  const [title, setTitle] = useState(notice.title || "");
  const [body, setBody] = useState(notice.body || "");
  const [accent, setAccent] = useState(notice.accent || member.accent_color || "#141414");
  const [expiresAt, setExpiresAt] = useState(notice.expires_at || "");
  const [dirty, setDirty] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const track = (fn) => (e) => { fn(e.target.value); setDirty(true); };
  const isLive = notice.status === "published";
  const usePhoto = notice.use_photo === true;

  const patch = async (extra = {}) => {
    const r = await client.patch(`/admin/notices/${notice.id}`, { type, title, body, accent, expires_at: expiresAt, ...extra });
    onUpdated(r.data);
    return r.data;
  };

  const save = async () => {
    setBusy(`save-${notice.id}`);
    try {
      await patch();
      setDirty(false);
      toast.success("Offer saved.");
    } catch { toast.error("Save failed."); }
    setBusy("");
  };

  const generatePoster = async () => {
    if (!window.confirm("Generate a stylish AI poster for this offer? Uses the member's logo, brand colour, and Master Photos as references. Spends AI credits.")) return;
    setBusy(`gen-${notice.id}`);
    try {
      // save current fields first so the poster uses the latest text
      if (dirty) { await patch(); setDirty(false); }
      const r = await client.post(`/admin/notices/${notice.id}/generate-poster`);
      onUpdated({ ...notice, ...r.data });
      toast.success("Poster generated — logo, colour and offer text stamped on.");
    } catch (e) { toast.error(e.response?.data?.detail || "Poster generation failed."); }
    setBusy("");
  };

  const setPosterStyle = async (photo) => {
    if (photo === usePhoto) return;
    setBusy(`style-${notice.id}`);
    try {
      const updated = await patch({ use_photo: photo });
      onUpdated(updated);
      toast.success(photo ? "Photo poster selected." : "Graphic poster selected — clean branded card, no photo needed.");
    } catch { toast.error("Couldn't switch style."); }
    setBusy("");
  };

  const swapPoster = async (path) => {
    setBusy(`swap-${notice.id}`);
    try {
      const updated = await patch({ poster_image: path, use_photo: true });
      onUpdated(updated);
      setShowPicker(false);
      toast.success("Poster photo swapped — no credits spent. Logo + offer text still overlay on top.");
    } catch { toast.error("Couldn't swap."); }
    setBusy("");
  };

  const draftCopy = async () => {
    setBusy(`copy-${notice.id}`);
    try {
      const r = await client.post(`/admin/notices/${notice.id}/draft-copy`, { hint: title || "" });
      if (r.data.title) setTitle(r.data.title);
      if (r.data.body) setBody(r.data.body);
      setDirty(true);
      toast.success("Offer copy drafted — edit if you want, then Save.");
    } catch (e) { toast.error(e.response?.data?.detail || "Draft failed."); }
    setBusy("");
  };

  const publishNow = async () => {
    if (!title || !body) { toast.error("Add a title and body first."); return; }
    if (usePhoto && !notice.poster_image) { toast.error("Generate or swap in the poster photo first — or switch to the Graphic style."); return; }
    if (!window.confirm("Publish this offer live on the Noticeboard now?")) return;
    setBusy(`pub-${notice.id}`);
    try {
      const updated = await patch({ status: "published" });
      onUpdated(updated);
      toast.success("Offer published live on the Noticeboard.");
    } catch { toast.error("Publish failed."); }
    setBusy("");
  };

  const unpublish = async () => {
    if (!window.confirm("Take this offer down from the Noticeboard? It goes back to draft — nothing is deleted.")) return;
    setBusy(`unpub-${notice.id}`);
    try {
      const updated = await patch({ status: "draft" });
      onUpdated(updated);
      toast.success("Offer taken down.");
    } catch { toast.error("Couldn't take down."); }
    setBusy("");
  };

  const anyBusy = !!busy;

  return (
    <div className="border border-ink/15 bg-white" data-testid={`noticeboard-card-${notice.id}`}>
      <div className="p-5 sm:p-6 grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
        {/* Poster preview */}
        <div>
          <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
            <p className="overline text-ink/60">Poster</p>
            <div className="flex gap-1.5">
              <button onClick={() => setPosterStyle(true)} disabled={anyBusy} data-testid={`noticeboard-style-photo-${notice.id}`}
                className={`px-3 py-1.5 overline text-[10px] border transition-colors ${usePhoto ? "border-gloss-gold bg-gloss-gold text-ink" : "border-ink/30 text-ink/60 hover:border-ink"}`}>
                Photo
              </button>
              <button onClick={() => setPosterStyle(false)} disabled={anyBusy} data-testid={`noticeboard-style-graphic-${notice.id}`}
                className={`px-3 py-1.5 overline text-[10px] border transition-colors ${!usePhoto ? "border-gloss-gold bg-gloss-gold text-ink" : "border-ink/30 text-ink/60 hover:border-ink"}`}>
                Graphic
              </button>
            </div>
          </div>
          {usePhoto ? (
            <div className="relative aspect-[3/4] bg-ink border border-ink/15 overflow-hidden" data-testid={`noticeboard-poster-${notice.id}`}>
              {notice.poster_image ? (
                <img src={imgUrl({ cover_image: notice.poster_image })} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-cream/40">
                  <ImageIcon size={28} />
                  <p className="overline text-[10px]">no poster yet</p>
                </div>
              )}
              {busy === `gen-${notice.id}` && (
                <div className="absolute inset-0 bg-ink/80 flex flex-col items-center justify-center gap-3 text-cream">
                  <RefreshCw size={22} className="animate-spin" />
                  <p className="overline text-[10px]">Stamping logo + brand colour…</p>
                </div>
              )}
            </div>
          ) : (
            <div className="relative aspect-[3/4] border border-ink/15 overflow-hidden grain" style={{ backgroundColor: accent }} data-testid={`noticeboard-graphic-preview-${notice.id}`}>
              <div className="absolute inset-0 p-5 flex flex-col">
                <p className="font-serif text-cream text-2xl">S/ME</p>
                <p className="overline text-[9px] text-gloss-gold mt-8">Reader {type}</p>
                <p className="font-serif text-cream text-2xl leading-tight mt-2">{title || "Your offer title"}</p>
                <div className="mt-auto flex justify-between items-end gap-2">
                  <p className="overline text-[9px] text-cream/80 truncate">{member.business_name}</p>
                  <p className="overline text-[9px] text-cream/60 shrink-0">{member.borough}</p>
                </div>
              </div>
            </div>
          )}
          {usePhoto ? (
            <>
              <button onClick={generatePoster} disabled={anyBusy}
                className="mt-3 w-full bg-gloss-gold text-ink px-4 py-3 overline flex items-center justify-center gap-2 hover:bg-ink hover:text-cream transition-colors disabled:opacity-40" data-testid={`noticeboard-generate-${notice.id}`}>
                {busy === `gen-${notice.id}` ? <RefreshCw size={14} className="animate-spin" /> : <Sparkles size={14} />} {notice.poster_image ? "Regenerate poster (credits)" : "Generate poster (credits)"}
              </button>
              <button onClick={() => setShowPicker(!showPicker)} disabled={anyBusy}
                className="mt-2 w-full border border-ink px-4 py-2.5 overline flex items-center justify-center gap-2 hover:bg-ink hover:text-cream transition-colors disabled:opacity-60" data-testid={`noticeboard-swap-${notice.id}`}>
                <Images size={14} /> {showPicker ? "Close picker" : "Swap poster photo (free)"}
              </button>
              <label className="mt-2 w-full border border-ink px-4 py-2.5 overline flex items-center justify-center gap-2 cursor-pointer hover:bg-ink hover:text-cream transition-colors" data-testid={`noticeboard-upload-${notice.id}`}>
                <Upload size={14} /> {busy === `upl-${notice.id}` ? "Uploading…" : "Upload your own poster (free)"}
                <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  setBusy(`upl-${notice.id}`);
                  const fd = new FormData();
                  fd.append("file", file);
                  try {
                    const r = await client.post(`/admin/notices/${notice.id}/poster`, fd);
                    onUpdated({ ...notice, ...r.data });
                    toast.success("Your poster is on the notice — no AI, no credits.");
                  } catch { toast.error("Upload failed — try again."); }
                  setBusy(""); e.target.value = "";
                }} data-testid={`noticeboard-upload-input-${notice.id}`} />
              </label>
              {showPicker && (
                <SavedImagePicker member={member} current={notice.poster_image} tid={`noticeboard-swap-${notice.id}`} busy={busy}
                  note="Pick any saved image as the poster photo — logo, colour and offer text still overlay on top. Free, no credits."
                  onPick={swapPoster} />
              )}
              <p className="font-mono text-[10px] text-ink/40 mt-2">Logo + brand colour + offer text stamped on by the template — never AI-drawn.</p>
            </>
          ) : (
            <p className="font-mono text-[10px] text-ink/40 mt-3">Clean branded card in the accent colour — S/ME masthead, offer title and business name. No photo, no AI, ready to publish as-is.</p>
          )}
        </div>

        {/* Editable fields */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => { setType("offer"); setDirty(true); }} data-testid={`noticeboard-type-offer-${notice.id}`}
              className={`px-3 py-1.5 overline text-[10px] border transition-colors ${type === "offer" ? "border-gloss-gold bg-gloss-gold text-ink" : "border-ink/30 text-ink/60 hover:border-ink"}`}>
              <Megaphone size={11} className="inline-block mr-1" /> Offer
            </button>
            <button onClick={() => { setType("event"); setDirty(true); }} data-testid={`noticeboard-type-event-${notice.id}`}
              className={`px-3 py-1.5 overline text-[10px] border transition-colors ${type === "event" ? "border-gloss-gold bg-gloss-gold text-ink" : "border-ink/30 text-ink/60 hover:border-ink"}`}>
              <Calendar size={11} className="inline-block mr-1" /> Event
            </button>
            <button onClick={draftCopy} disabled={anyBusy}
              className="ml-auto px-3 py-1.5 overline text-[10px] bg-ink text-cream flex items-center gap-1.5 hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-60" data-testid={`noticeboard-draft-copy-${notice.id}`}>
              {busy === `copy-${notice.id}` ? <RefreshCw size={11} className="animate-spin" /> : <Sparkles size={11} />} Draft with AI
            </button>
          </div>
          <div>
            <label className="overline text-[10px] text-ink/60 block mb-1">Title (short, punchy)</label>
            <input value={title} onChange={track(setTitle)} maxLength={60} placeholder="e.g. 20% off your first wholesale order"
              className="w-full border border-ink/30 px-4 py-2.5 font-serif text-lg focus:outline-none focus:border-ink" data-testid={`noticeboard-title-${notice.id}`} />
            <p className="font-mono text-[9px] text-ink/40 mt-1">{title.length}/60</p>
          </div>
          <div>
            <label className="overline text-[10px] text-ink/60 block mb-1">Body (one-line detail)</label>
            <textarea value={body} onChange={track(setBody)} maxLength={180} rows={3} placeholder="e.g. Wholesale-only. Minimum £250. Ends 31st March. UK-wide delivery."
              className="w-full border border-ink/30 px-4 py-2.5 font-sans text-sm focus:outline-none focus:border-ink" data-testid={`noticeboard-body-${notice.id}`} />
            <p className="font-mono text-[9px] text-ink/40 mt-1">{body.length}/180</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="overline text-[10px] text-ink/60 block mb-1">Accent colour</label>
              <div className="flex items-center gap-2">
                <input type="color" value={accent} onChange={track(setAccent)}
                  className="w-10 h-10 border border-ink/30 cursor-pointer" data-testid={`noticeboard-accent-${notice.id}`} />
                <input value={accent} onChange={track(setAccent)}
                  className="flex-1 border border-ink/30 px-2 py-2 font-mono text-[11px] focus:outline-none focus:border-ink" />
              </div>
            </div>
            <div>
              <label className="overline text-[10px] text-ink/60 block mb-1">Expires (optional)</label>
              <input type="date" value={expiresAt ? expiresAt.slice(0, 10) : ""} onChange={track(setExpiresAt)}
                className="w-full border border-ink/30 px-3 py-2 font-mono text-[12px] focus:outline-none focus:border-ink" data-testid={`noticeboard-expires-${notice.id}`} />
            </div>
          </div>
          <div className="pt-2 border-t border-ink/10 flex items-center gap-2 flex-wrap justify-end">
            <span className={`overline text-[10px] mr-auto ${isLive ? "text-green-700" : dirty ? "text-gloss-gold" : "text-ink/40"}`} data-testid={`noticeboard-status-${notice.id}`}>
              {dirty ? "Unsaved" : isLive ? "· LIVE ·" : "Draft"}
            </span>
            <button onClick={onRemove} disabled={anyBusy}
              className="border border-ink/20 text-ink/60 p-2 hover:border-red-600 hover:text-red-600 transition-colors disabled:opacity-40" title="Delete offer" data-testid={`noticeboard-delete-${notice.id}`}>
              <Trash2 size={13} />
            </button>
            <button onClick={save} disabled={anyBusy || !dirty}
              className="border border-ink px-4 py-2 overline flex items-center gap-2 hover:bg-ink hover:text-cream transition-colors disabled:opacity-40" data-testid={`noticeboard-save-${notice.id}`}>
              {busy === `save-${notice.id}` ? <RefreshCw size={13} className="animate-spin" /> : <Save size={13} />} Save
            </button>
            {isLive ? (
              <>
                <a href="/noticeboard" target="_blank" rel="noreferrer" className="border border-ink/30 text-ink/70 px-3 py-2 overline flex items-center gap-1.5 hover:border-ink hover:text-ink transition-colors" data-testid={`noticeboard-view-${notice.id}`}>
                  <Eye size={13} /> View live
                </a>
                <button onClick={unpublish} disabled={anyBusy}
                  className="border border-ink/30 text-ink/70 px-3 py-2 overline hover:border-ink hover:text-ink transition-colors disabled:opacity-40" data-testid={`noticeboard-unpublish-${notice.id}`}>
                  {busy === `unpub-${notice.id}` ? <RefreshCw size={13} className="animate-spin" /> : "Take down"}
                </button>
              </>
            ) : (
              <button onClick={publishNow} disabled={anyBusy || !title || !body || (usePhoto && !notice.poster_image)}
                className="bg-gloss-gold text-ink px-5 py-2 overline flex items-center gap-2 hover:bg-ink hover:text-cream transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-testid={`noticeboard-publish-${notice.id}`}>
                {busy === `pub-${notice.id}` ? <RefreshCw size={13} className="animate-spin" /> : <Send size={13} />} Publish now
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
